import { Map, List, OrderedMap } from 'immutable';
import { toUpper } from 'ramda';
import moment from 'moment';
import {
  SERVICE_PER_PERSON_TYPES,
  SERVICE_PER_NIGHT_TYPES,
  SERVICE_TYPE_PER_USE,
  SERVICE_TYPE_PER_PERSON,
  SERVICE_TYPE_PER_STAY
} from '../config/constants';

export const addServiceByTime = (blockIdx, service, reservationBlocks, date, addServiceByTime) => {

    let serviceBlock = OrderedMap();
    let serviceDates = List();

    let quantity = 1;
    let price = service.get('price');

    const block = reservationBlocks.find(block => block.get('idx') === blockIdx);
    const currentService = block.get('services', List()).find(serviceBlock => serviceBlock.get('service_id') === service.get('id'));
    
    if(service.get('max_per_room', false)) {
      if(currentService && currentService.get('quantity', 0) >= service.get('max_per_room')) {
        return;
      }
    }

    const serviceType = toUpper(String(service?.get("type") || ''));
    const isPerson = SERVICE_PER_PERSON_TYPES.includes(serviceType);
    const worksWithQuota = !!service.get("works_with_quota", false);
    const worksWithQuotaTime = !!service.get("works_with_quota_time", false);

    if(isPerson) {
      quantity = block.get('adults') + block.get('childs', 0);
    }

    if(worksWithQuota) {
      serviceDates = serviceDates.push(Map({
        "quantity": quantity,
        "day": date.format('YYYY-MM-DD'),
        "time": worksWithQuotaTime ? date.format('HH:mm:ss') : null
      }));

      let day = service.get('days').find(day => day.get('day') === date.format('YYYY-MM-DD'));
      price = day.get('price');

      if(isPerson) price *= quantity;
    }

    const blockBody = Map({
      service_id: service.get('id'),
      price: price,
      quantity: quantity,
      dates: serviceDates
    });

    serviceBlock = serviceBlock.set(
      blockIdx,
      blockBody
    );

    addServiceByTime(serviceBlock);

    return blockBody;
}

/**
 * Construye el bloque de un servicio SIN cupo.
 * Forma: Map({ service_id, price, quantity }).
 */
export const buildSimpleServiceBlock = (service, quantity = 1) =>
  Map({
    service_id: service.get('id'),
    price: service.get('price'),
    quantity: quantity
  });

/**
 * Construye el bloque de un servicio CON cupo SIN cupo horario (selección por fecha).
 *
 * @param service          servicio (Immutable Map) con su config y 'days'
 * @param block            bloque de reserva destino (para ocupación en tipos por persona)
 * @param selectionServices List de entradas seleccionadas para este servicio (cada una con 'date' y opcional 'quantity')
 * @returns Map({ service_id, price, quantity, dates })
 */
export const buildDateServiceBlock = (service, block, selectionServices) => {
  const serviceType = toUpper(service.get('type'));
  const isPerson = SERVICE_PER_PERSON_TYPES.includes(serviceType);

  const serviceDates = selectionServices.reduce((dates, selService) => {
    const date = selService.get('date');
    const datePrice = service.get('days', List()).find(day => day.get('day') === date);

    if (!datePrice) return dates;

    const quantity = isPerson
      ? block.get('adults', 0) + block.get('childs', 0)
      : parseInt(selService.get('quantity'), 10);

    return dates.push(
      Map({
        quantity: quantity,
        day: date,
        price: datePrice.get('price') * quantity
      })
    );
  }, List());

  const quantity = serviceDates.reduce((total, date) => total + date.get('quantity'), 0);
  const price = serviceDates.reduce((total, date) => total + date.get('price'), 0);

  return Map({
    service_id: service.get('id'),
    price: price,
    quantity: quantity,
    dates: serviceDates
  });
};

export const SELECTION_ERROR_NOT_FOUND = 'NOT_FOUND';
export const SELECTION_ERROR_INVALID_FORMAT = 'INVALID_FORMAT';
export const SELECTION_ERROR_NOT_AVAILABLE = 'NOT_AVAILABLE';

const findAvailService = (availServicesList, serviceCode) => {
  const parsed = parseInt(serviceCode, 10);
  return !isNaN(parsed)
    ? availServicesList.find(s => s.get('id') === parsed)
    : availServicesList.find(s => s.get('code') === serviceCode);
};

// Step 1: descarta servicios que no existen en availServices (por código o id)
export const filterSelection = (availServices) => ({ selection, fullSuccess, errors = [] }) => {
  const availServicesList = availServices.get('services', List());
  let resultFullSuccess = fullSuccess;
  let resultErrors = errors;

  const filteredSelection = selection.map((block, blockIdx) => {
    const services = block.get('services', List());
    const filteredServices = services.filter(service => {
      const code = service.get('code');
      const found = findAvailService(availServicesList, code);
      if (!found) {
        resultFullSuccess = false;
        resultErrors = [...resultErrors, SELECTION_ERROR_NOT_FOUND];
      }
      return !!found;
    });
    return block.set('services', filteredServices);
  });

  return { selection: filteredSelection, fullSuccess: resultFullSuccess, errors: resultErrors };
};

// Step 2: valida el formato de cada servicio según su tipo/configuración
export const validateFormat = (availServices) => ({ selection, fullSuccess, errors = [] }) => {
  const availServicesList = availServices.get('services', List());
  let resultFullSuccess = fullSuccess;
  let resultErrors = errors;

  const validatedSelection = selection.map((block, blockIdx) => {
    const services = block.get('services', List());
    const validatedServices = services.filter(service => {
      const code = service.get('code');
      const availService = findAvailService(availServicesList, code);
      if (!availService) return false;
      const type = toUpper(String(availService?.get("type") || ''));
      const worksWithQuota = availService.get('works_with_quota', false);
      const worksWithQuotaTime = availService.get('works_with_quota_time', false);

      const hasQuantity = !!service.get('quantity');
      const hasDate = !!service.get('date');
      const hasHour = !!service.get('time');

      let isValid = true;

      if (type === SERVICE_TYPE_PER_USE) {
        if (!worksWithQuota) {
          isValid = hasQuantity && !hasDate && !hasHour;
        } else if (!worksWithQuotaTime) {
          isValid = hasQuantity && hasDate && !hasHour;
        } else {
          isValid = hasQuantity && hasDate && hasHour;
        }
      } else if (type === SERVICE_TYPE_PER_PERSON) {
        if (!worksWithQuota) {
          isValid = !hasQuantity && !hasDate && !hasHour;
        } else if (!worksWithQuotaTime) {
          isValid = !hasQuantity && hasDate && !hasHour;
        } else {
          isValid = !hasQuantity && hasDate && hasHour;
        }
      } else if (type === SERVICE_TYPE_PER_STAY) {
        isValid = !hasQuantity && !hasDate && !hasHour;
      } else if (SERVICE_PER_NIGHT_TYPES.includes(type)) {
        if (!worksWithQuotaTime) {
          isValid = !hasQuantity && !hasDate && !hasHour;
        } else {
          isValid = false;
        }
      }

      if (!isValid) {
        resultFullSuccess = false;
        resultErrors = [...resultErrors, SELECTION_ERROR_INVALID_FORMAT];
      }
      return isValid;
    });
    return block.set('services', validatedServices);
  });

  return { selection: validatedSelection, fullSuccess: resultFullSuccess, errors: resultErrors };
};

// Step 3: valida cupo disponible para los servicios uqe lo requieren.
export const validateQuota = (availServices, reservationBlocks) => ({ selection, fullSuccess, errors = [] }) => {
  const availServicesList = availServices.get('services', List());
  let resultFullSuccess = fullSuccess;
  let resultErrors = errors;

  const quotaValidatedSelection = selection.map((block, blockIdx) => {
    const services = block.get('services', List());
    const validatedServices = services.filter(service => {
      const code = service.get('code');
      const availService = findAvailService(availServicesList, code);
      if (!availService) return false;

      const type = toUpper(String(availService?.get("type") || ''));
      const worksWithQuota = availService.get('works_with_quota', false);
      const worksWithQuotaTime = availService.get('works_with_quota_time', false);

      const isQuotaType = type === SERVICE_TYPE_PER_USE || type === SERVICE_TYPE_PER_PERSON;
      if (!isQuotaType || !worksWithQuota) {
        return true;
      }

      // PER_USE lleva cantidad explícita; PER_PERSON usa la ocupación del bloque (adults + childs)
      const reservationBlock = reservationBlocks.find(b => b.get('idx') === blockIdx + 1);
      const quantity = type === SERVICE_TYPE_PER_USE
        ? parseInt(service.get('quantity'), 10)
        : (reservationBlock ? reservationBlock.get('adults', 0) + reservationBlock.get('childs', 0) : 0);
      const date = service.get('date');
      const day = availService.get('days', List()).find((blockDay) => date === blockDay.get('day'));

      if (!day) {
        resultFullSuccess = false;
        resultErrors = [...resultErrors, SELECTION_ERROR_NOT_AVAILABLE];
        return false;
      }

      // Con cupo horario el cupo está por hora en quotas_time; validamos el de la hora solicitada
      if (worksWithQuotaTime) {
        const targetHour = parseInt(service.get('time'), 10);
        const quotaTime = day.get('quotas_time', List()).find(qt => moment(qt.get('time'), 'HH:mm:ss').hour() === targetHour);
        const availableQuota = quotaTime ? quotaTime.get('quota', 0) : null;

        if (!quotaTime || availableQuota < quantity) {
          resultFullSuccess = false;
          resultErrors = [...resultErrors, SELECTION_ERROR_NOT_AVAILABLE];
          return false;
        }

        return true;
      }

      // Cupo diario
      const availableQuota = day.get('quota', 0);

      if (availableQuota < quantity) {
        resultFullSuccess = false;
        resultErrors = [...resultErrors, SELECTION_ERROR_NOT_AVAILABLE];
        return false;
      }

      return true;
    });
    return block.set('services', validatedServices);
  });

  return { selection: quotaValidatedSelection, fullSuccess: resultFullSuccess, errors: resultErrors };
};

// Step 4: finalmente, despues de todos los filtros procedemos a añadir a la reserva los que han quedado.
export const addToBlocks = (availServices, reservationBlocks, checkin, numberOfNights, servicesActions) => ({ selection, fullSuccess, errors = [] }) => {
  const servicesLeft = selection.some(block => block.get('services', List()).size);

  if (!servicesLeft) {
    return { selection, fullSuccess, errors };
  }

  const availServicesList = availServices.get('services', List());
  const { addService, addServiceByTime: addServiceByTimeFn } = servicesActions;

  selection.forEach((selBlock, segIdx) => {
    const blockIdx = segIdx + 1;
    const reservationBlock = reservationBlocks.find(b => b.get('idx') === blockIdx);

    if (!reservationBlock) {
      return;
    }

    // Agrupamos las entradas del segmento por código (mismo código => mismo servicio => un solo bloque con varias dates)
    const services = selBlock.get('services', List());
    const groupedByCode = services.groupBy(service => service.get('code'));

    groupedByCode.forEach((entries, code) => {
      const availService = findAvailService(availServicesList, code);
      if (!availService) return;

      const type = toUpper(String(availService?.get("type") || ''));
      const worksWithQuota = availService.get('works_with_quota', false);
      const worksWithQuotaTime = availService.get('works_with_quota_time', false);

      // Por noche / por noche-persona CON cupo
      if (SERVICE_PER_NIGHT_TYPES.includes(type) && worksWithQuota) {
        if (worksWithQuotaTime) {
          // Con cupo HORARIO: fuera de este desarrollo
          return;
        }
        // Con cupo (sin horas): se imputa en CADA noche de la estancia (la URL solo trae el código).
        // El util addServiceByTime crea una date por noche (time=null), deriva la cantidad
        // (1 por noche / adults+childs por noche-persona) y el reducer ADD_SERVICE_BY_TIME acumula.
        for (let i = 0; i < numberOfNights; i++) {
          addServiceByTime(blockIdx, availService, reservationBlocks, moment(checkin).add(i, 'days'), addServiceByTimeFn);
        }
        return;
      }

      if (type === SERVICE_TYPE_PER_STAY || SERVICE_PER_NIGHT_TYPES.includes(type)) {
        // Por estancia / por noche / por noche-persona SIN cupo: solo el código, sin cantidad/fecha/hora.
        // El bloque es idéntico (quantity 1, precio base); la multiplicación por noches/personas la resuelve
        // el backend según el tipo, igual que la UI (AvailServiceDetail, rama else). "Se comportan como por estancia".
        addService(OrderedMap().set(blockIdx, buildSimpleServiceBlock(availService, 1)));
      } else if (!worksWithQuota) {
        // Sin cupo (PER_USE / PER_PERSON): quantity = suma de cantidades (PER_USE) o 1 (PER_PERSON)
        const quantity = type === SERVICE_TYPE_PER_USE
          ? entries.reduce((total, e) => total + parseInt(e.get('quantity'), 10), 0)
          : 1;
        const serviceBlock = OrderedMap().set(blockIdx, buildSimpleServiceBlock(availService, quantity));
        addService(serviceBlock);
      } else if (!worksWithQuotaTime) {
        // Con cupo, sin horario: un solo bloque con las dates de todas las entradas
        const serviceBlock = OrderedMap().set(blockIdx, buildDateServiceBlock(availService, reservationBlock, entries));
        addService(serviceBlock);
      } else {
        // Con cupo horario: el util añade 1 unidad por llamada y el reducer acumula cantidad/precio/dates.
        // PER_USE lleva la cantidad en la URL (llamamos N veces); PER_PERSON la deriva el util de la ocupación (1 llamada).
        entries.forEach(entry => {
          const date = entry.get('date');
          const time = entry.get('time');
          const when = moment(`${date} ${time}`, 'YYYY-MM-DD H');
          const units = type === SERVICE_TYPE_PER_USE ? parseInt(entry.get('quantity'), 10) : 1;
          for (let i = 0; i < units; i++) {
            addServiceByTime(blockIdx, availService, reservationBlocks, when, addServiceByTimeFn);
          }
        });
      }
    });
  });

  return { selection, fullSuccess, errors };
};